package se.mittpass.shared.util

import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn

/**
 * Normalizes a Swedish personal identity number (personnummer) or coordination number (samordningsnummer)
 * into standard YYYYMMDD-XXXX format.
 *
 * Supported inputs:
 * - 12 digits with hyphen: "19840630-3514" -> "19840630-3514"
 * - 12 digits without hyphen: "198406303514" -> "19840630-3514"
 * - 10 digits with hyphen: "840630-3514" -> "19840630-3514"
 * - 10 digits without hyphen: "8406303514" -> "19840630-3514"
 * - 10 digits with plus: "240630+3514" -> "19240630-3514" (centenarians)
 *
 * Returns null if the format is invalid.
 */
fun normalizePersonnummer(raw: String): String? {
    val clean = raw.replace("\\s+".toRegex(), "")
    if (clean.isBlank()) return null

    val currentYear = try {
        Clock.System.todayIn(TimeZone.currentSystemDefault()).year
    } catch (_: Throwable) {
        2026
    }
    val currentYear2Digit = currentYear % 100

    return when {
        // 12 digits with hyphen: YYYYMMDD-XXXX
        clean.matches(Regex("^\\d{8}-\\d{4}$")) -> clean

        // 12 digits without hyphen: YYYYMMDDXXXX
        clean.matches(Regex("^\\d{12}$")) -> "${clean.substring(0, 8)}-${clean.substring(8)}"

        // 10 digits with plus: YYMMDD+XXXX (100+ years old)
        clean.matches(Regex("^\\d{6}\\+\\d{4}$")) -> {
            val yy = clean.substring(0, 2).toInt()
            val century = if (yy <= currentYear2Digit) (currentYear / 100) - 1 else (currentYear / 100) - 2
            "$century${clean.substring(0, 6)}-${clean.substring(7)}"
        }

        // 10 digits with hyphen: YYMMDD-XXXX
        clean.matches(Regex("^\\d{6}-\\d{4}$")) -> {
            val yy = clean.substring(0, 2).toInt()
            val century = if (yy <= currentYear2Digit) (currentYear / 100) else (currentYear / 100) - 1
            "$century${clean.substring(0, 6)}-${clean.substring(7)}"
        }

        // 10 digits without hyphen: YYMMDDXXXX
        clean.matches(Regex("^\\d{10}$")) -> {
            val yy = clean.substring(0, 2).toInt()
            val century = if (yy <= currentYear2Digit) (currentYear / 100) else (currentYear / 100) - 1
            "$century${clean.substring(0, 6)}-${clean.substring(6)}"
        }

        else -> null
    }
}
